import { useState, useEffect } from "react"
import BaseModal from "../BaseComponents/BaseModal/BaseModal"
import ToggleSwitch from "../BaseComponents/Toggle/ToggleSwitch"
import SelectBox from "../BaseComponents/SelectBox/SelectBox"
import { useToast } from "../../context/ToastContext"
import { useUpdatePage } from "../../api/cms/usePagesApi"
import styles from "./EditPageModal.module.css"
import {
  FileText,
  Settings,
  Globe,
  Search,
  ToggleRight,
  FileCode2
} from "lucide-react"

const META_ROBOTS_OPTIONS = [
  { value: "INDEX_FOLLOW", label: "Index, Follow" },
  { value: "NOINDEX_FOLLOW", label: "No Index, Follow" },
  { value: "NOINDEX_NOFOLLOW", label: "No Index, No Follow" },
]

const PAGE_CATEGORY_OPTIONS = [
  { value: "static", label: "Static Page" },
  { value: "content", label: "Content Page" },
]

const CONTENT_TYPE_OPTIONS = [
  { value: "destination", label: "Destination Page" },
  { value: "blog", label: "Blog Article" },
  { value: "inspiration", label: "Travel Inspiration" },
  { value: "campaign", label: "Campaign / Landing Page" },
]

const EditPageModal = ({ open, onClose, page, onSuccess }) => {
  const { showToast } = useToast()
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [canonicalUrl, setCanonicalUrl] = useState("")
  const [metaTitle, setMetaTitle] = useState("")
  const [metaDescription, setMetaDescription] = useState("")
  const [content, setContent] = useState("")
  const [sortOrder, setSortOrder] = useState(0)
  const [pageCategory, setPageCategory] = useState("static")
  const [contentType, setContentType] = useState("")
  const [metaRobots, setMetaRobots] = useState("INDEX_FOLLOW")
  const [status, setStatus] = useState(true)

  const { execute: updatePage, loading: updating, error: updateError } = useUpdatePage()

  useEffect(() => {
    if (page && open) {
      setTitle(page.title ?? "")
      setSlug(page.slug ?? "")
      setCanonicalUrl(page.canonicalUrl ?? "")
      setMetaTitle(page.metaTitle ?? "")
      setMetaDescription(page.metaDescription ?? "")
      setContent(page.content ?? "")
      setSortOrder(page.sortOrder ?? 0)
      setPageCategory((page.pageCategory || "STATIC").toLowerCase())
      setContentType(page.contentType ?? "")
      setMetaRobots(page.metaRobots ?? "INDEX_FOLLOW")
      setStatus(page.status === "PUBLISHED" || page.status === "Published")
    }
  }, [page, open])

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!page?.id) return
    try {
      await updatePage(page.id, {
        pageCategory: pageCategory.toUpperCase(),
        contentType: pageCategory === "content" && contentType ? contentType : null,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        status: status ? "PUBLISHED" : "DRAFT",
        canonicalUrl: canonicalUrl.trim() || null,
        metaRobots,
        descriptions: [
          {
            languageCode: "en",
            title: title.trim() || "Untitled",
            slug: slug.trim() || "/",
            metaTitle: metaTitle.trim() || null,
            metaDescription: metaDescription.trim() || null,
            content: content.trim() || null,
          },
        ],
      })
      showToast("Page updated successfully", "success")
      onSuccess?.()
      onClose?.()
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update page", "error")
    }
  }

  const modalTitle = page ? `Edit Page: ${page.title || "Untitled"}` : "Edit Page"

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      width="900px"
      title={modalTitle}
      footer={
        <div className={styles.footerActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSubmit}
            disabled={updating || !page?.id}
          >
            {updating ? "Saving…" : "Save Changes"}
          </button>
        </div>
      }
    >
      <p className={styles.subtitle}>
        Update page settings, SEO metadata, and content
      </p>

      {updateError && (
        <div className={styles.error}>{updateError}</div>
      )}

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <FileText size={16} />
          <span>Page Setup</span>
        </div>

        <label className={styles.label}>Page Category</label>
        <SelectBox
          width="100%"
          placeholder="Select page category"
          value={pageCategory}
          options={PAGE_CATEGORY_OPTIONS}
          onChange={(e) => {
            setPageCategory(e.target.value)
            setContentType("")
          }}
        />

        {pageCategory === "content" && (
          <>
            <label className={styles.label}>Content Type</label>
            <SelectBox
              width="100%"
              placeholder="Select content type"
              value={contentType}
              options={CONTENT_TYPE_OPTIONS}
              onChange={(e) => setContentType(e.target.value)}
            />
          </>
        )}

        <label className={styles.label}>Sort Order</label>
        <input
          className={styles.input}
          type="number"
          min={0}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <Settings size={16} />
          <span>Basic Information</span>
        </div>

        <label className={styles.label}>Page Title</label>
        <input
          className={styles.input}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <div className={styles.row}>
          <div className={styles.col}>
            <label className={styles.label}>Slug / URL</label>
            <div className={styles.inputWithIcon}>
              <span className={styles.prefix}>#</span>
              <input
                className={styles.input}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.col}>
            <label className={styles.label}>Canonical URL</label>
            <div className={styles.inputWithIcon}>
              <Globe size={14} className={styles.inputIcon} />
              <input
                className={styles.input}
                value={canonicalUrl}
                onChange={(e) => setCanonicalUrl(e.target.value)}
                placeholder="https://example.com/page-slug"
              />
            </div>
          </div>
        </div>
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <Search size={16} />
          <span>SEO Settings</span>
        </div>

        <label className={styles.label}>Meta Title</label>
        <input
          className={styles.input}
          value={metaTitle}
          onChange={(e) => setMetaTitle(e.target.value)}
          placeholder="Meta title"
        />
        <span className={styles.helper}>{metaTitle.length} / 60 characters</span>

        <label className={styles.label}>Meta Description</label>
        <textarea
          className={styles.textarea}
          rows={3}
          value={metaDescription}
          onChange={(e) => setMetaDescription(e.target.value)}
          placeholder="Meta description"
        />
        <span className={styles.helper}>{metaDescription.length} / 160 characters</span>

        <label className={styles.label}>Meta Robots</label>
        <SelectBox
          width="100%"
          placeholder="Select meta robots"
          value={metaRobots}
          options={META_ROBOTS_OPTIONS}
          onChange={(e) => setMetaRobots(e.target.value)}
        />
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <FileCode2 size={16} />
          <span>Page Content</span>
        </div>

        <label className={styles.label}>Content (HTML / Text)</label>
        <textarea
          className={`${styles.textarea} ${styles.contentTextarea}`}
          rows={12}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Enter complete page content here"
        />
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <ToggleRight size={16} />
          <span>Publishing Status</span>
        </div>

        <div className={styles.statusRow}>
          <div>
            <p className={styles.statusTitle}>Page Status</p>
            <p className={styles.statusDesc}>
              Page is live and visible to users
            </p>
          </div>

          <ToggleSwitch
            checked={status}
            onChange={() => setStatus(!status)}
          />
        </div>
      </section>
    </BaseModal>
  )
}

export default EditPageModal
